/**
 * Integrations — credentials other systems use to write here.
 *
 * The screen has one job the rest of Settings does not: it shows a secret
 * exactly once, and has to make that believable. Everything about the layout
 * below follows from it — the token appears in its own panel with a copy
 * button, the panel says plainly that it will not be shown again, and it
 * disappears from the list the moment anything else is done.
 *
 * The temptation is to be kind and keep it retrievable. That would make it
 * retrievable by anyone who reaches this screen, forever, which is a worse
 * trade than asking somebody to rotate a key they mislaid.
 */

import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, RefreshCw, ShieldOff } from 'lucide-react';
import type { IntegrationCredential, IssuedIntegrationCredential } from '@cenox/shared';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  Modal,
  TableSkeleton,
} from '../../components/ui';
import { api } from '../../lib/api';
import { useAuth, useBusinessPath } from '../../lib/auth';
import { IntegrationHealth } from './IntegrationHealth';

function when(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

export function IntegrationSettings(): ReactNode {
  const path = useBusinessPath();
  const { can } = useAuth();
  const qc = useQueryClient();
  const mayManage = can('settings:manage');

  const [name, setName] = useState('');
  const [issued, setIssued] = useState<IssuedIntegrationCredential | null>(null);
  const [revoking, setRevoking] = useState<IntegrationCredential | null>(null);

  const list = useQuery({
    queryKey: ['integrations', path('')],
    queryFn: () => api.get<{ data: IntegrationCredential[] }>(path('/integrations')),
  });

  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ['integrations', path('')] });
  };

  const create = useMutation({
    mutationFn: () =>
      api.post<{ data: IssuedIntegrationCredential }>(path('/integrations'), { name }),
    onSuccess: (res) => {
      setIssued(res.data);
      setName('');
      invalidate();
    },
  });

  const rotate = useMutation({
    mutationFn: (id: string) =>
      api.post<{ data: IssuedIntegrationCredential }>(path(`/integrations/${id}/rotate`), {}),
    onSuccess: (res) => {
      setIssued(res.data);
      invalidate();
    },
  });

  const revoke = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.post(path(`/integrations/${id}/revoke`), { reason }),
    onSuccess: () => {
      setRevoking(null);
      invalidate();
    },
  });

  if (list.isError) {
    return <ErrorState message="Could not load integrations." onRetry={() => void list.refetch()} />;
  }

  const rows = list.data?.data ?? [];

  return (
    <div className="space-y-5">
      <Card>
        <div className="space-y-1">
          <h2 className="text-sm font-semibold text-slate-900">Integrations</h2>
          <p className="text-sm text-slate-600">
            A credential lets another system — a dispatch backend, a WhatsApp bot, an ordering
            app — record deliveries into this business. It writes here and reads almost nothing:
            it cannot see your sales, your reports or your settings.
          </p>
        </div>

        {mayManage && (
          <form
            className="mt-4 flex flex-wrap items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (name.trim()) create.mutate();
            }}
          >
            <div className="min-w-56 flex-1">
              <Input
                label="What is it for"
                placeholder="Ceno Hub dispatch"
                value={name}
                onChange={(e) => setName(e.target.value)}
                hint="A name you will recognise in the activity log."
              />
            </div>
            <Button type="submit" disabled={create.isPending || name.trim() === ''}>
              Issue a credential
            </Button>
          </form>
        )}

        {create.error ? <ErrorState message={(create.error as Error).message} /> : null}
      </Card>

      {/* Above the credential list: "is it working" is asked far more often than "which key". */}
      {rows.some((c) => c.isActive) && <IntegrationHealth />}

      {list.isPending ? (
        <TableSkeleton />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No integrations yet"
          description="Nothing is writing deliveries into this business from outside."
        />
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="py-2">Name</th>
                  <th className="py-2">Key</th>
                  <th className="py-2">Last used</th>
                  <th className="py-2">Status</th>
                  {mayManage && <th className="py-2 text-right">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((c) => (
                  <tr key={c.id}>
                    <td className="py-2.5 font-medium text-slate-900">
                      {c.name}
                      <div className="text-xs font-normal text-slate-500">
                        Issued {when(c.createdAt)}
                        {c.createdByName ? ` by ${c.createdByName}` : ''}
                      </div>
                    </td>
                    <td className="py-2.5 font-mono text-xs text-slate-500">{c.keyId}</td>
                    <td className="py-2.5 text-xs text-slate-500">
                      {/* The question somebody wiring this up actually has. */}
                      {c.lastUsedAt ? when(c.lastUsedAt) : 'Never connected'}
                    </td>
                    <td className="py-2.5">
                      {c.isActive ? (
                        <Badge tone="success">Active</Badge>
                      ) : (
                        <Badge tone="neutral">Revoked{c.revokedReason ? ` — ${c.revokedReason}` : ''}</Badge>
                      )}
                    </td>
                    {mayManage && (
                      <td className="py-2.5 text-right">
                        {c.isActive && (
                          <div className="flex justify-end gap-1.5">
                            <Button
                              size="sm"
                              variant="secondary"
                              icon={<RefreshCw className="size-3.5" />}
                              disabled={rotate.isPending}
                              onClick={() => rotate.mutate(c.id)}
                            >
                              Rotate
                            </Button>
                            <Button
                              size="sm"
                              variant="secondary"
                              icon={<ShieldOff className="size-3.5" />}
                              onClick={() => setRevoking(c)}
                            >
                              Revoke
                            </Button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {issued && <IssuedToken credential={issued} onClose={() => setIssued(null)} />}
      {revoking && (
        <RevokeCredential
          credential={revoking}
          onClose={() => setRevoking(null)}
          onConfirm={(reason) => revoke.mutate({ id: revoking.id, reason })}
          pending={revoke.isPending}
        />
      )}
    </div>
  );
}

/**
 * The one moment the secret exists in the open.
 *
 * Deliberately a modal that has to be dismissed, rather than a line in the
 * table that scrolls away: closing it is the acknowledgement that it was
 * copied, and there is no second chance to ask for.
 */
function IssuedToken({
  credential,
  onClose,
}: {
  credential: IssuedIntegrationCredential;
  onClose: () => void;
}): ReactNode {
  const [copied, setCopied] = useState(false);

  return (
    <Modal open onClose={onClose} title={`${credential.name} is ready`} size="lg">
      <div className="space-y-4">
        <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          Copy this now. It is not stored and will not be shown again — if it is lost, rotate the
          credential to get a new one.
        </p>

        <div>
          <div className="mb-1 text-sm font-medium text-slate-700">Token</div>
          <code className="block overflow-x-auto rounded-lg border border-slate-200 bg-slate-50 p-3 font-mono text-xs text-slate-800">
            {credential.token}
          </code>
        </div>

        <div className="rounded-lg border border-slate-200 p-3 text-sm text-slate-600">
          <div className="mb-1 font-medium text-slate-700">How the sending system uses it</div>
          <code className="block overflow-x-auto font-mono text-xs">
            Authorization: Bearer {credential.token.slice(0, 16)}…
          </code>
          <p className="mt-2">
            Deliveries are posted to <code className="font-mono text-xs">/api/v1/ingest/deliveries/events</code>.
            There is no business in that address — this credential decides which one it writes to.
          </p>
        </div>

        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            onClick={() => {
              void navigator.clipboard?.writeText(credential.token).then(() => setCopied(true));
            }}
          >
            {copied ? 'Copied' : 'Copy token'}
          </Button>
          <Button onClick={onClose}>Done</Button>
        </div>
      </div>
    </Modal>
  );
}

function RevokeCredential({
  credential,
  onClose,
  onConfirm,
  pending,
}: {
  credential: IntegrationCredential;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  pending: boolean;
}): ReactNode {
  const [reason, setReason] = useState('');

  return (
    <Modal open onClose={onClose} title={`Revoke ${credential.name}?`} size="md">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (reason.trim()) onConfirm(reason.trim());
        }}
      >
        <p className="text-sm text-slate-600">
          It stops working immediately. Nothing it already recorded changes — the deliveries it
          wrote stay exactly where they are, and this credential stays listed as the explanation
          for them.
        </p>
        <Input
          label="Why"
          required
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Replaced by a new credential"
        />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="danger"
            icon={<KeyRound className="size-4" />}
            disabled={pending || reason.trim() === ''}
          >
            Revoke
          </Button>
        </div>
      </form>
    </Modal>
  );
}
